/**
 * Edit Follow Modal
 *
 * 팔로우 설정 편집 모달
 * - 복사 비율 수정
 * - 할당 금액 수정
 * - 최소 금액 검증
 * - 리스크 경고 표시
 *
 * @see docs/COPY_TRADING_FRONTEND_PLAN.md
 */

import React, { useState, useContext, useEffect } from 'react';
import { Modal, Button, Slider, InputNumber, message } from 'antd';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ThemeContext } from '@/theme/ThemeProvider';
import { useIsTablet } from '@utils/CalVW.tsx';
import { formatDecimal } from '@utils/formatDecimal';

import type { CopyFollow, CopyPortfolio } from '../../../../types/copyTrading';
import { useCopyPortfolioStore } from '../../../hooks/copyTrading/copyPortfolioStore';

interface EditFollowModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  follow: CopyFollow | null;
  portfolio: CopyPortfolio | null;
}

export const EditFollowModal: React.FC<EditFollowModalProps> = ({
  open,
  setOpen,
  follow,
  portfolio,
}) => {
  const { t } = useTranslation();
  const isTablet = useIsTablet();
  const { isDarkMode } = useContext(ThemeContext);
  const theme = isDarkMode ? 'dark' : 'light';

  const { updateFollowSettings, isLoading } = useCopyPortfolioStore();

  // Form State
  const [copyRatio, setCopyRatio] = useState<number>(50);
  const [allocatedAmount, setAllocatedAmount] = useState<number>(0);

  // Reset form when modal opens
  useEffect(() => {
    if (open && follow && portfolio) {
      setCopyRatio(follow.copyRatio * 100);  // Convert to percentage
      setAllocatedAmount(follow.allocatedAmount);
    }
  }, [open, follow, portfolio]);

  if (!follow || !portfolio) {
    return null;
  }

  const handleCancel = () => {
    setOpen(false);
  };

  const handleConfirm = async () => {
    // Validation
    if (allocatedAmount < portfolio.minCopyAmount) {
      message.error(
        t('minCopyAmountError', { amount: formatDecimal(portfolio.minCopyAmount, 2) })
      );
      return;
    }

    if (copyRatio < 1 || copyRatio > 100) {
      message.error(t('copyRatioRangeError'));
      return;
    }

    // 변경사항 확인
    const hasChanged =
      copyRatio / 100 !== follow.copyRatio || allocatedAmount !== follow.allocatedAmount;

    if (!hasChanged) {
      message.info(t('noChanges'));
      setOpen(false);
      return;
    }

    try {
      await updateFollowSettings({
        followId: follow.id,
        copyRatio: copyRatio / 100, // Convert percentage to decimal
        allocatedAmount,
      });

      message.success(t('followSettingsUpdated'));
      setOpen(false);
    } catch (error: any) {
      message.error(error.message || t('followSettingsUpdateFailed'));
    }
  };

  // 변경 감지
  const hasChanged =
    copyRatio / 100 !== follow.copyRatio || allocatedAmount !== follow.allocatedAmount;

  // 리스크 경고 (고레버리지 경고)
  const isHighRisk = copyRatio > 80 || allocatedAmount > 10000;

  return (
    <Modal
      open={open}
      width={isTablet ? '90%' : '500px'}
      centered={true}
      closable={true}
      onCancel={handleCancel}
      footer={[
        <Button key="back" onClick={handleCancel} disabled={isLoading}>
          {t('cancel')}
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={isLoading}
          onClick={handleConfirm}
          disabled={!hasChanged}
        >
          {t('editSettings')}
        </Button>,
      ]}
      title={t('editFollowTitle', { username: portfolio.leaderUsername })}
      className={'p2pmodal' + (theme === 'dark' ? ' dark' : '')}
    >
      <ModalContents theme={theme}>
        {/* 포트폴리오 정보 */}
        <InfoSection>
          <InfoRow>
            <InfoLabel theme={theme}>{t('leader')}</InfoLabel>
            <InfoValue theme={theme}>{portfolio.leaderUsername}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel theme={theme}>{t('portfolio')}</InfoLabel>
            <InfoValue theme={theme}>{portfolio.portfolioName}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel theme={theme}>{t('profitShareRate')}</InfoLabel>
            <InfoValue theme={theme} highlight>{formatDecimal(portfolio.profitShareRate, 2)}%</InfoValue>
          </InfoRow>
        </InfoSection>

        <Divider theme={theme} />

        {/* 복사 비율 설정 */}
        <FormSection>
          <FormLabel theme={theme}>
            {t('copyRatio')}
            <span style={{ color: '#3b82f6', marginLeft: 8 }}>{copyRatio}%</span>
            {copyRatio / 100 !== follow.copyRatio && (
              <ChangeBadge theme={theme}>
                ({t('previousValue')}: {formatDecimal(follow.copyRatio * 100, 0)}%)
              </ChangeBadge>
            )}
          </FormLabel>
          <FormDesc theme={theme}>{t('copyRatioDesc')}</FormDesc>
          <Slider
            min={1}
            max={100}
            value={copyRatio}
            onChange={(value) => setCopyRatio(value)}
            marks={{
              1: '1%',
              25: '25%',
              50: '50%',
              75: '75%',
              100: '100%',
            }}
            tooltip={{ formatter: (value) => `${value}%` }}
          />
        </FormSection>

        {/* 할당 금액 입력 */}
        <FormSection>
          <FormLabel theme={theme}>
            {t('allocatedAmountUSDT')}
            {allocatedAmount !== follow.allocatedAmount && (
              <ChangeBadge theme={theme}>
                ({t('previousValue')}: {formatDecimal(follow.allocatedAmount, 2)} USDT)
              </ChangeBadge>
            )}
          </FormLabel>
          <FormDesc theme={theme}>
            {t('minAmount')}: {formatDecimal(portfolio.minCopyAmount, 2)} USDT
          </FormDesc>
          <StyledInputNumber
            min={portfolio.minCopyAmount}
            max={1000000}
            value={allocatedAmount}
            onChange={(value) => setAllocatedAmount(Number(value) || 0)}
            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            parser={(value) => value?.replace(/\$\s?|(,*)/g, '') as any}
            style={{ width: '100%' }}
          />
        </FormSection>

        <Divider theme={theme} />

        {/* 예상 정보 */}
        <SummarySection theme={theme}>
          <SummaryRow>
            <SummaryLabel theme={theme}>{t('newCopyRatio')}</SummaryLabel>
            <SummaryValue theme={theme}>{copyRatio}%</SummaryValue>
          </SummaryRow>
          <SummaryRow>
            <SummaryLabel theme={theme}>{t('newAllocatedAmount')}</SummaryLabel>
            <SummaryValue theme={theme}>{formatDecimal(allocatedAmount, 2)} USDT</SummaryValue>
          </SummaryRow>
          <SummaryRow>
            <SummaryLabel theme={theme}>{t('profitShareRate')}</SummaryLabel>
            <SummaryValue theme={theme} highlight>
              {formatDecimal(portfolio.profitShareRate, 2)}%
            </SummaryValue>
          </SummaryRow>
        </SummarySection>

        {/* 변경사항 경고 */}
        {hasChanged && (
          <InfoBox theme={theme}>
            {t('settingsChangeNote')}
          </InfoBox>
        )}

        {/* 리스크 경고 */}
        {isHighRisk && (
          <WarningBox theme={theme}>
            ⚠️ <strong>{t('highRiskWarning')}</strong>
            <br />
            {t('highRiskWarningDesc')}
          </WarningBox>
        )}
      </ModalContents>
    </Modal>
  );
};

// ==========================================
// Styled Components
// ==========================================

const ModalContents = styled.div<{ theme?: string }>`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 10px 0;
  background-color: ${({ theme }) => (theme === 'dark' ? '#1a1a1a' : '#fff')};
  border-radius: 10px;
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const InfoLabel = styled.div<{ theme?: string }>`
  font-size: 14px;
  color: ${({ theme }) => (theme === 'dark' ? '#a0a0a0' : '#666')};
  opacity: 0.9;
`;

const InfoValue = styled.div<{ highlight?: boolean; theme?: string }>`
  font-size: 14px;
  font-weight: 600;
  color: ${({ highlight, theme }) =>
    highlight ? '#3b82f6' : (theme === 'dark' ? '#e5e5e5' : '#1a1a1a')};
`;

const Divider = styled.div<{ theme?: string }>`
  height: 1px;
  background-color: ${({ theme }) => (theme === 'dark' ? '#333' : '#e0e0e0')};
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FormLabel = styled.div<{ theme?: string }>`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => (theme === 'dark' ? '#e5e5e5' : '#1a1a1a')};
`;

const ChangeBadge = styled.span<{ theme?: string }>`
  font-size: 12px;
  font-weight: 400;
  color: ${({ theme }) => (theme === 'dark' ? '#fbbf24' : '#f59e0b')};
  margin-left: 8px;
`;

const FormDesc = styled.div<{ theme?: string }>`
  font-size: 13px;
  color: ${({ theme }) => (theme === 'dark' ? '#a0a0a0' : '#666')};
  opacity: 0.9;
  margin-bottom: 8px;
`;

const StyledInputNumber = styled(InputNumber)`
  height: 40px;
  font-size: 16px;
  font-weight: 600;

  .ant-input-number-input {
    height: 100%;
    font-weight: 600;
  }
`;

const SummarySection = styled.div<{ theme?: string }>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  border-radius: 8px;
  background-color: ${({ theme }) => (theme === 'dark' ? '#222' : '#f5f5f5')};
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SummaryLabel = styled.div<{ theme?: string }>`
  font-size: 14px;
  color: ${({ theme }) => (theme === 'dark' ? '#a0a0a0' : '#666')};
`;

const SummaryValue = styled.div<{ highlight?: boolean; theme?: string }>`
  font-size: 14px;
  font-weight: 700;
  color: ${({ highlight, theme }) =>
    highlight ? '#3b82f6' : (theme === 'dark' ? '#e5e5e5' : '#1a1a1a')};
`;

const InfoBox = styled.div<{ theme?: string }>`
  padding: 12px;
  border-radius: 8px;
  background-color: ${({ theme }) => (theme === 'dark' ? '#1e3a5f' : '#dbeafe')};
  color: ${({ theme }) => (theme === 'dark' ? '#93c5fd' : '#1e40af')};
  font-size: 13px;
  line-height: 1.6;
  border-left: 4px solid #3b82f6;
`;

const WarningBox = styled.div<{ theme?: string }>`
  padding: 12px;
  border-radius: 8px;
  background-color: ${({ theme }) => (theme === 'dark' ? '#3d2f1f' : '#fef3c7')};
  color: ${({ theme }) => (theme === 'dark' ? '#fbbf24' : '#92400e')};
  font-size: 13px;
  line-height: 1.6;
  border-left: 4px solid #f59e0b;
`;
